import {Button, IconButton, SelectPanel, type SelectPanelItemInput} from '@primer/react'
import type {RegisteredRuleSchemaComponent} from '../../types/rules-types'
import {Blankslate} from '../Blankslate'
import {PlusIcon, TrashIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useCallback, useMemo, useState} from 'react'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useDebounce} from '@github-ui/use-debounce'
import {DEBOUNCE_DELAY_MS} from '../../helpers/constants'

type Actor = {
  name: string
  globalRelayId: string
  avatarUrl: string
  type: string
}

type ActorMetadata = {
  actors: Record<string, Actor>
}

export function ActorSelector({
  field,
  value,
  onValueChange,
  readOnly,
  metadata,
  queryMetadata,
}: RegisteredRuleSchemaComponent) {
  if (!Array.isArray(value)) {
    throw new Error('Value must be an array')
  }

  const [actorMetadata, setActorMetadata] = useState<Record<string, Actor>>((metadata as ActorMetadata)?.actors || {})

  const selected = useMemo(() => value.map(v => actorMetadata[v]).filter(s => s !== undefined), [value, actorMetadata])

  const onValueUpdate = useCallback(
    (newSelected: Actor[]) => {
      const newValues = newSelected.map(option => option.globalRelayId)
      onValueChange?.(newValues)
    },
    [onValueChange],
  )

  const getActorSuggestions = useCallback(
    async (filter: string) => {
      try {
        const metadataResult = await queryMetadata?.(`actor_suggestions:${filter}`)
        const actors = (metadataResult as {actors: Actor[]})?.actors || []

        const actorsById = actors.reduce(
          (acc, actor) => {
            acc[actor.globalRelayId] = actor
            return acc
          },
          {} as Record<string, Actor>,
        )
        setActorMetadata(existing => ({...existing, ...actorsById}))

        return actors
      } catch {
        return []
      }
    },
    [queryMetadata],
  )

  return (
    <>
      {!readOnly ? (
        <div className="Box">
          <div className="Box-header d-flex flex-justify-between flex-items-center">
            <div className="Box-title">{field.display_name}</div>
            <ActorPicker value={selected} onValueChange={onValueUpdate} getActorSuggestions={getActorSuggestions} />
          </div>
          <ActorRows selectedActors={selected} onValueChange={onValueUpdate} readOnly={readOnly} />
        </div>
      ) : (
        <ActorRows selectedActors={selected} onValueChange={onValueUpdate} readOnly={readOnly} />
      )}
    </>
  )
}

function ActorPicker({
  value,
  onValueChange,
  getActorSuggestions,
}: {
  value: Actor[]
  onValueChange: (value: Actor[]) => void
  getActorSuggestions: (filter: string) => Promise<Actor[]>
}) {
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<Actor[]>([])

  const items: SelectPanelItemInput[] = useMemo(() => {
    const mappedSuggestions = suggestions.map(s => ({
      id: s.globalRelayId,
      text: s.name,
      description: s.type,
      leadingVisual: () => <GitHubAvatar src={s.avatarUrl} />,
      onAction: () => {
        const alreadySelected = value.find(u => u.globalRelayId === s.globalRelayId)
        if (alreadySelected) {
          onValueChange(value.filter(u => u.globalRelayId !== s.globalRelayId))
        } else {
          onValueChange([...value, s])
        }
      },
    }))
    return mappedSuggestions
  }, [suggestions, value, onValueChange])

  const selectedItems = useMemo(() => {
    return items.filter(item => value.map(v => v.globalRelayId).includes(item.id as string))
  }, [items, value])

  const debounceSuggestions = useDebounce(async (newFilter: string) => {
    setIsLoading(true)
    setSuggestions(await getActorSuggestions(newFilter))
    setIsLoading(false)
  }, DEBOUNCE_DELAY_MS)

  const onOpenChange = async () => {
    if (!open) {
      setFilter('')
      setOpen(true)
      setIsLoading(true)
      setSuggestions(await getActorSuggestions(''))
      setIsLoading(false)
    } else {
      setOpen(false)
    }
  }

  return (
    <SelectPanel
      title="Add actors"
      loading={isLoading}
      open={open}
      onOpenChange={onOpenChange}
      filterValue={filter}
      onFilterChange={f => {
        setFilter(f)
        debounceSuggestions(f)
      }}
      items={items}
      selected={selectedItems}
      placeholderText="Select actors"
      renderAnchor={anchorProps => {
        return (
          <Button
            trailingAction={TriangleDownIcon}
            leadingVisual={PlusIcon}
            aria-label={`Click to add actors`}
            {...anchorProps}
          >
            Add actors
          </Button>
        )
      }}
      onSelectedChange={() => {}} // using onAction
    />
  )
}

function ActorRows({
  selectedActors,
  onValueChange,
  readOnly,
}: {
  selectedActors: Actor[]
  onValueChange?: (value: Actor[]) => void
  readOnly?: boolean
}) {
  return (
    <>
      {selectedActors.length ? (
        <ul>
          {selectedActors.map(actor => (
            <li key={actor.globalRelayId} className="Box-row d-flex flex-justify-between flex-items-center">
              <div>
                {actor.avatarUrl && <GitHubAvatar src={actor.avatarUrl} />}
                <span className="ml-2 wb-break-word">{actor.name}</span>
              </div>
              {!readOnly && (
                <IconButton
                  className="ml-2"
                  icon={TrashIcon}
                  type="button"
                  aria-label={`Delete ${actor.name}`}
                  size="small"
                  variant="invisible"
                  onClick={() => {
                    onValueChange?.(selectedActors.filter(toDelete => toDelete.globalRelayId !== actor.globalRelayId))
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      ) : (
        <Blankslate heading="No values have been added" />
      )}
    </>
  )
}

try{ ActorSelector.displayName ||= 'ActorSelector' } catch {}
try{ ActorPicker.displayName ||= 'ActorPicker' } catch {}
try{ ActorRows.displayName ||= 'ActorRows' } catch {}